import { useRef, useState } from "react";
import type { DragEvent, ChangeEvent } from "react";
import { supabase } from "@/lib/supabase";

const MAX_SIZE = 5 * 1024 * 1024; // 5 Mo
const ALLOWED_TYPES = [
  "image/jpeg",
  "image/png",
  "image/webp",
  "image/gif",
  "image/avif",
];

interface ImageUploadProps {
  value: string;
  onChange: (url: string) => void;
  placeholder?: string;
  required?: boolean;
}

export default function ImageUpload({
  value,
  onChange,
  placeholder = "Glissez une image ici ou cliquez pour parcourir",
  required = false,
}: ImageUploadProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [dragOver, setDragOver] = useState(false);

  function cleanFileName(name: string): string {
    const base = name
      .replace(/\.[^.]+$/, "")
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase()
      .replace(/[^a-z0-9_-]+/g, "-")
      .replace(/^-+|-+$/g, "")
      .slice(0, 40);
    const ext = name.split(".").pop()?.toLowerCase() ?? "jpg";
    const unique = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    return `${base || "image"}-${unique}.${ext}`;
  }

  async function uploadFile(file: File) {
    setError("");
    if (!ALLOWED_TYPES.includes(file.type)) {
      setError("Format non supporté. Utilisez JPG, PNG, WebP, GIF ou AVIF.");
      return;
    }
    if (file.size > MAX_SIZE) {
      setError("Image trop lourde (5 Mo maximum).");
      return;
    }

    setUploading(true);
    try {
      const path = `${cleanFileName(file.name)}`;
      const { error: uploadError } = await supabase.storage
        .from("images")
        .upload(path, file, { cacheControl: "3600", upsert: false });

      if (uploadError) {
        setError(uploadError.message);
        return;
      }

      const { data } = supabase.storage.from("images").getPublicUrl(path);
      onChange(data.publicUrl);
    } catch {
      setError("Une erreur est survenue pendant le téléversement.");
    } finally {
      setUploading(false);
    }
  }

  function handleSelect(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (file) uploadFile(file);
    e.target.value = "";
  }

  function handleDrop(e: DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) uploadFile(file);
  }

  return (
    <div className="space-y-2">
      <div
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
        onClick={() => inputRef.current?.click()}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            inputRef.current?.click();
          }
        }}
        className={`relative flex items-center gap-4 rounded-md border-2 border-dashed p-3 cursor-pointer transition-colors ${
          dragOver
            ? "border-primary-500 bg-primary-100/60"
            : "border-background-300 bg-background-50 hover:border-primary-400 hover:bg-background-100"
        }`}
      >
        <input
          ref={inputRef}
          type="file"
          accept={ALLOWED_TYPES.join(",")}
          className="hidden"
          onChange={handleSelect}
        />

        {value ? (
          <div className="w-16 h-16 shrink-0 rounded-md overflow-hidden border border-background-200 bg-background-100">
            <img
              src={value}
              alt="Aperçu"
              className="w-full h-full object-cover object-top"
            />
          </div>
        ) : (
          <span className="w-16 h-16 shrink-0 flex items-center justify-center rounded-md bg-background-100 text-foreground-400">
            <i className="ri-image-add-line text-2xl"></i>
          </span>
        )}

        <div className="flex-1 min-w-0">
          {uploading ? (
            <span className="inline-flex items-center gap-2 text-sm font-medium text-primary-600">
              <i className="ri-loader-4-line animate-spin"></i>
              Téléversement en cours…
            </span>
          ) : (
            <>
              <p className="text-sm font-medium text-foreground-700">
                {value ? "Remplacer l'image" : "Importer une image"}
              </p>
              <p className="text-xs text-foreground-500 mt-0.5">
                {placeholder} — JPG, PNG, WebP, GIF ou AVIF (5 Mo max)
              </p>
            </>
          )}
        </div>

        {value && !uploading && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onChange("");
            }}
            aria-label="Retirer l'image"
            className="w-8 h-8 shrink-0 flex items-center justify-center rounded-full text-foreground-500 hover:bg-primary-100 hover:text-primary-600 transition cursor-pointer"
          >
            <i className="ri-close-line"></i>
          </button>
        )}
      </div>

      <div className="flex items-center gap-2">
        <span className="text-xs text-foreground-500">ou collez une URL :</span>
        <input
          type="url"
          required={required}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="https://…"
          className="flex-1 rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400 transition"
        />
      </div>

      {error && (
        <p className="text-xs font-medium text-accent-700">
          <i className="ri-error-warning-line mr-1"></i>
          {error}
        </p>
      )}
    </div>
  );
}